import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { isJsonPayload } from '../../../shared/api/payload';
import { actionErrorText, toastError } from '../../../shared/toastHelper';
import Popup from 'reactjs-popup';
import { ToastContainer, toast } from 'react-toastify';

import { canCreateConfirm } from '../../../shared/offerConfirmEligibility';
import CrmTable from '../../shared/components/CrmTable';
import CrmTableStateRow from '../../shared/components/CrmTableStateRow';
import CrmSearchInput from '../../shared/components/CrmSearchInput';
import FilterSidebar from '../../shared/components/FilterSidebar';
import ActiveFilterBadges from '../../shared/components/ActiveFilterBadges';
import TableSettings from '../../shared/components/TableSettings';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import CrmAvatar from '../../shared/components/CrmAvatar';
import { Button as SetlistButton, Plus, Sliders02 } from '@opendate-app/setlist';

import V3CreateConfirmModal from '../../OfferEditUI/components/V3CreateConfirmModal';
import LinkOfferModalApp from '../../LinkOfferModal/startup/LinkOfferModalApp';
import OfferModalApp from '../../OfferModal/startup/OfferModalApp';
import OfferV2ModalApp from '../../OfferV2Modal/startup/OfferV2ModalApp';
import OfferV2PreviewApp from '../../OfferV2Preview/startup/OfferV2PreviewApp';
import PipelineSlidingPaneApp from '../../PipelineSlidingPane/startup/PipelineSlidingPaneApp';
import ProcessingJobsSidebar from '../../shared/components/ProcessingJobsSidebar';
import { subscribeToDocumentProcessingJobs } from '../../../channels/document_processing_job_channel';
import { canLinkToEvent } from '../../../shared/offerLinkEligibility';

import { FORM_STATE, PREVIEW_STATE } from '../../OfferModal/constants/offerModalConstants';
import { OFFER_COLUMNS, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/offerColumnsConfig';
import { OFFER_FILTER_GROUPS, getActiveFilterCount } from '../constants/offerFiltersConfig';
import '../styles/offerManagerStyles.css';

const qs = require('qs');

const STORAGE_KEY = 'offerManagerColumns';

const modalContentStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '800px',
  padding: '0px',
};

const linkOfferModalContentStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '700px',
  padding: '0',
};

const deleteModalContentStyle = {
  textAlign: 'left',
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '500px',
  padding: '15px',
};

const popupContentStyle = {
  border: 'none',
  borderRadius: '8px',
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  width: '250px',
  padding: '0px',
  zIndex: 9999,
};

const OfferManager = ({
  venuesOptions,
  csrfToken,
  team,
  currentUser,
  offerTemplates,
  venueSelected,
  venueOwnerships,
  offerExists,
  rooms,
  offerV3,
}) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [unfilteredTotal, setUnfilteredTotal] = useState(null);
  const perPage = 25;

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [venueNameOptions, setVenueNameOptions] = useState([]);
  const [genreOptions, setGenreOptions] = useState([]);
  const [buyerOptions, setBuyerOptions] = useState([]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [offer, setOffer] = useState({});
  const [offerToLink, setOfferToLink] = useState({});
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [pipeline, setPipeline] = useState({});

  const [processingJobs, setProcessingJobs] = useState([]);
  const [processingJobsSidebarOpen, setProcessingJobsSidebarOpen] = useState(false);

  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const initColumns = () => {
    let visible = getDefaultVisibleColumns();
    let hidden = getDefaultHiddenColumns();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.visible) visible = parsed.visible;
        if (parsed.hidden) hidden = parsed.hidden;
      }
    } catch (e) { }

    const configById = OFFER_COLUMNS.reduce((map, col) => { map[col.id] = col; return map; }, {});
    visible = visible.map(c => configById[c.id] ? { ...c, label: configById[c.id].label } : c);
    hidden = hidden.map(c => configById[c.id] ? { ...c, label: configById[c.id].label } : c);

    const knownIds = new Set([...visible.map(c => c.id), ...hidden.map(c => c.id)]);
    OFFER_COLUMNS.forEach((col) => {
      if (!knownIds.has(col.id)) {
        if (col.defaultVisible) {
          visible.push(col);
        } else {
          hidden.push(col);
        }
      }
    });

    return { visible, hidden };
  };

  const [visibleColumns, setVisibleColumns] = useState(() => initColumns().visible);
  const [hiddenColumns, setHiddenColumns] = useState(() => initColumns().hidden);

  const [refreshCounter, setRefreshCounter] = useState(0);
  const tableSettingsRef = useRef(null);

  const enrichedFilterGroups = useMemo(() => {
    return OFFER_FILTER_GROUPS.map((group) => ({
      ...group,
      filters: group.filters.map((filter) => {
        if (filter.id === 'owner' && ownerOptions) {
          return { ...filter, options: ownerOptions };
        }
        if (filter.id === 'venue_name' && venueNameOptions) {
          return { ...filter, options: venueNameOptions };
        }
        if (filter.id === 'genre' && genreOptions) {
          return { ...filter, options: genreOptions };
        }
        if (filter.id === 'buyer' && buyerOptions) {
          return { ...filter, options: buyerOptions };
        }
        return filter;
      }),
    }));
  }, [ownerOptions, venueNameOptions, genreOptions, buyerOptions]);

  const isColumnVisible = useCallback(
    (colId) => visibleColumns.some((c) => c.id === colId),
    [visibleColumns]
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ visible: visibleColumns, hidden: hiddenColumns }));
  }, [visibleColumns, hiddenColumns]);


  useEffect(() => {
    const params = new URL(document.location).searchParams;
    const offerPipelineId = params.get('offer');
    if (offerPipelineId) {
      axios.get(`/teams/${team.id}/pipelines/${offerPipelineId}`)
        .then(({ data }) => setPipeline(data));
    }
  }, []);

  useEffect(() => {
    if (!team?.id) return;
    axios.get(`/teams/${team.id}/offers/filter_options.json`)
      .then(({ data }) => {
        setOwnerOptions(data.ownerOptions || []);
        setVenueNameOptions(data.venueNameOptions || []);
        setGenreOptions(data.genreOptions || []);
        setBuyerOptions(data.buyerOptions || []);
      });
  }, [team?.id]);


  useEffect(() => {
    if (!team?.id || !team.package?.ai_features) return;

    const subscription = subscribeToDocumentProcessingJobs(team.id, {
      received: (data) => {
        setProcessingJobs((prevJobs) => {
          const existingIndex = prevJobs.findIndex((j) => j.id === data.id);
          if (existingIndex >= 0) {
            const updated = [...prevJobs];
            updated[existingIndex] = { ...prevJobs[existingIndex], ...data };
            return updated;
          }
          return [data, ...prevJobs];
        });

        if (data.status === 'completed') {
          setRefreshCounter((c) => c + 1);
        }
      },
    });

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [team?.id, team?.package?.ai_features]);


  const buildQueryParams = useCallback(() => {
    const q = {};
    if (searchTerm) q.keyword_search = searchTerm;
    if (activeFilters.state && activeFilters.state.length > 0) {
      q.state_in = activeFilters.state;
    }
    if (activeFilters.owner && activeFilters.owner.length > 0) {
      q.owner_in = activeFilters.owner;
    }
    if (activeFilters.act_type && activeFilters.act_type.length > 0) {
      q.act_type_in = activeFilters.act_type;
    }
    if (activeFilters.has_event && activeFilters.has_event.length > 0) {
      q.has_event_in = activeFilters.has_event;
    }
    if (activeFilters.has_holds && activeFilters.has_holds.length > 0) {
      q.has_holds_in = activeFilters.has_holds;
    }
    if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
      q.venue_name_in = activeFilters.venue_name;
    }
    if (activeFilters.genre && activeFilters.genre.length > 0) {
      q.genre_in = activeFilters.genre;
    }
    if (activeFilters.buyer && activeFilters.buyer.length > 0) {
      q.buyer_in = activeFilters.buyer;
    }
    if (activeFilters.event_date) {
      if (activeFilters.event_date.start) q.event_date_after = activeFilters.event_date.start;
      if (activeFilters.event_date.end) q.event_date_before = activeFilters.event_date.end;
    }
    if (activeFilters.created_at) {
      if (activeFilters.created_at.start) q.created_after = activeFilters.created_at.start;
      if (activeFilters.created_at.end) q.created_before = activeFilters.created_at.end;
    }
    return q;
  }, [searchTerm, activeFilters]);


  useEffect(() => {
    if (!team?.id) return;

    const fetchOffers = async () => {
      try {
        setLoading(true);
        setError(null);
        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }
        const q = buildQueryParams();
        const response = await axios.get(`/teams/${team.id}/offers.json`, {
          params: {
            page: currentPage,
            per_page: perPage,
            ...(Object.keys(q).length > 0 ? { q } : {}),
          },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
        });

        const data = response.data || {};
        const offersData = data.offers || [];
        const pagination = data.pagination || {};

        const total = pagination.total_entries || offersData.length || 0;
        setOffers(offersData);
        setTotalEntries(total);
        setTotalPages(pagination.total_pages || 1);
        if (Object.keys(q).length === 0 || unfilteredTotal === null) {
          setUnfilteredTotal((prev) => prev === null ? total : prev);
        }
      } catch (e) {
        setError('Failed to load offers.');
        toast.error('Failed to load offers.', {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [team?.id, currentPage, searchTerm, activeFilters, refreshCounter]);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilters]);

  const refetch = () => setRefreshCounter((c) => c + 1);

  const handleAccept = async (offerRow) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const basePath = offerRow.ui_version === 3 ? 'offers/v3' : 'offers';
      const acceptResponse = await axios.post(`/teams/${team.id}/${basePath}/${offerRow.id}/accept.json`);
      if (!isJsonPayload(acceptResponse.data)) {
        toastError("Couldn't accept this offer. Please reload the page and try again.");
        return;
      }
      refetch();
    } catch (e) {
      toastError(actionErrorText(e, "Couldn't accept this offer. Please try again."));
    }
  };

  const handleDecline = async (offerRow) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const basePath = offerRow.ui_version === 3 ? 'offers/v3' : 'offers';
      const declineResponse = await axios.post(`/teams/${team.id}/${basePath}/${offerRow.id}/decline.json`);
      if (!isJsonPayload(declineResponse.data)) {
        toastError("Couldn't decline this offer. Please reload the page and try again.");
        return;
      }
      refetch();
    } catch (e) {
      toastError(actionErrorText(e, "Couldn't decline this offer. Please try again."));
    }
  };

  const handleDelete = async (offerRow) => {
    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      const basePath = offerRow.ui_version === 3 ? 'offers/v3' : 'offers';
      const deleteResponse = await axios.delete(`/teams/${team.id}/${basePath}/${offerRow.id}.json`);
      if (!isJsonPayload(deleteResponse.data)) {
        toastError("Couldn't delete this offer. Please reload the page and try again.");
        return;
      }
      refetch();
    } catch (e) {
      toastError(actionErrorText(e, "Couldn't delete this offer. Please try again."));
    }
  };

  const handlePreview = (offerRow) => {
    if (offerRow.ui_version === 3) {
      window.open(offerRow.preview_url, '_blank');
    } else {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      axios.get(`/teams/${team.id}/offers/${offerRow.id}.json`).then(({ data }) => {
        const offerData = { ...data, frontEndState: PREVIEW_STATE };
        setOffer(offerData);
        setModalIsOpen(true);
      });
    }
  };

  const handleEdit = (offerRow) => {
    if (offerRow.ui_version === 1) {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      axios.get(`/teams/${team.id}/offers/${offerRow.id}.json`).then(({ data }) => {
        const offerData = { ...data, frontEndState: FORM_STATE };
        setOffer(offerData);
        setModalIsOpen(true);
      });
    } else {
      window.location.href = offerRow.edit_url;
    }
  };

  const handleLinkToEvent = (offerRow) => {
    setOfferToLink({
      id: offerRow.id,
      ui_version: offerRow.ui_version,
      artist_or_event_name: offerRow.artist_or_event_name,
      calendar_event: offerRow.has_calendar_event ? { id: offerRow.calendar_event_id } : null,
    });
  };

  const closeModal = () => {
    setOffer({});
    setModalIsOpen(false);
  };

  const activeJobsCount = processingJobs.filter(
    (j) => j.status === 'pending' || j.status === 'processing'
  ).length;

  const activeFilterCount = getActiveFilterCount(activeFilters);

  const COLUMN_STYLES = {
    event: { width: '35%', minWidth: 300 },
    offer_created: { width: '12%' },
    owner: { width: '12%' },
    buyer: { width: '12%' },
    status: { width: '8%' },
    type: { width: '8%' },
    event_date: { width: '12%' },
    event_status: { width: '8%' },
    venue_name: { width: '15%' },
    holds: { width: '8%' },
    genre: { width: '15%' },
  };

  const columns = useMemo(() => {
    const cols = visibleColumns.map((col) => ({
      key: col.id,
      label: col.label,
      style: COLUMN_STYLES[col.id] || {},
    }));
    if (!isOverflowing) {
      cols.push({ key: 'actions', label: '', style: { width: '5%' }, className: 'text-right' });
    }
    return cols;
  }, [visibleColumns, isOverflowing]);

  const renderEmptyState = () => {
    if (offerExists) return null;
    return (
      <div className="row text-left pt-4">
        <div className="col-12">
          <div className="card no-border zero-offer-records-card">
            <div className="card-body">
              <div className="row">
                <div className="col-12 col-lg-8">
                  <h4 style={{ fontSize: '20px' }}>Create and Send an Offer!</h4>
                  <p className="text-muted pb-3">With Opendate, offer creation is super easy.</p>
                  <ol className="circled-numbers">
                    <li>
                      <h5 style={{ fontSize: '14px' }}>Enter your event details like venue, date, and artist</h5>
                      <p className="text-muted small">Select from existing confirms on your calendar or start fresh. You can also search our database of nearly 170k artists.</p>
                    </li>
                    <li>
                      <h5 style={{ fontSize: '14px' }}>Add your deal terms and any additional info</h5>
                      <p className="text-muted small">We've added a handful of terms to help inspire you and get you started. Edit, delete, and add your own.</p>
                    </li>
                    <li>
                      <h5 style={{ fontSize: '14px' }}>Save, preview and send</h5>
                      <p className="text-muted small">When you're ready, send it off to the agent for acceptance. Be sure to save the offer as a template to make it even easier the next time.</p>
                    </li>
                  </ol>
                  <h5 className="py-3" style={{ fontSize: '16px' }}>Get started and create your first offer in Opendate today!</h5>
                  <SetlistButton variant="primary" icon={Plus} onClick={() => setModalIsOpen(true)}>
                    Create Offer
                  </SetlistButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRowActions = (offerRow) => (
    <Popup
      arrow={false}
      offsetY={5}
      position="bottom right"
      className="offers-table-popup"
      contentStyle={popupContentStyle}
      onOpen={(e) => e.preventDefault()}
      trigger={(open) => (
        <a href="#" onClick={(e) => e.preventDefault()}>
          <i className="fas fa-ellipsis-v"></i>
        </a>
      )}
    >
      {(close) => (
        <div className="row text-left">
          <div className="col-12">
            <ul className="list-group offer-inline-menu highlight-primary">
              {offerRow.state !== 'accepted' && offerRow.state !== 'declined' && (
                <li className="list-group-item">
                  <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); close(); handleAccept(offerRow); }}>
                    <span className="d-inline-block text-center" style={{ width: '28px' }}>
                      <i className="fas fa-check-circle"></i>
                    </span>
                    Mark as Accepted
                  </a>
                </li>
              )}
              {offerRow.state !== 'accepted' && offerRow.state !== 'declined' && (
                <li className="list-group-item">
                  <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); close(); handleDecline(offerRow); }}>
                    <span className="d-inline-block text-center" style={{ width: '28px' }}>
                      <i className="far fa-times-circle"></i>
                    </span>
                    Mark as Declined
                  </a>
                </li>
              )}
              <li className="list-group-item">
                <a href={offerRow.pdf_url} target="_blank" className="text-muted">
                  <span className="d-inline-block text-center" style={{ width: '28px' }}>
                    <i className="fas fa-download"></i>
                  </span>
                  Download PDF
                </a>
              </li>
              <li className="list-group-item">
                <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); close(); handlePreview(offerRow); }}>
                  <span className="d-inline-block text-center" style={{ width: '28px' }}>
                    <i className="fas fa-search"></i>
                  </span>
                  Preview
                </a>
              </li>
              <li className="list-group-item">
                <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); close(); handleEdit(offerRow); }}>
                  <span className="d-inline-block text-center" style={{ width: '28px' }}>
                    <i className="fas fa-pencil"></i>
                  </span>
                  Edit
                </a>
              </li>
              {offerRow.ui_version === 3 && canCreateConfirm(offerRow, currentUser) && (
                <li className="list-group-item">
                  <a
                    href="#"
                    className="text-muted"
                    data-testid="pipeline-create-confirm"
                    onClick={(e) => { e.preventDefault(); close(); setConfirmOffer(offerRow); }}
                  >
                    <span className="d-inline-block text-center" style={{ width: '28px' }}>
                      <i className="fas fa-calendar-check"></i>
                    </span>
                    Create a Confirm
                  </a>
                </li>
              )}
              {canLinkToEvent(offerRow) && (
                <li className="list-group-item">
                  <a href="#" className="text-muted" onClick={(e) => { e.preventDefault(); close(); handleLinkToEvent(offerRow); }}>
                    <span className="d-inline-block text-center" style={{ width: '28px' }}>
                      <i className="fas fa-link"></i>
                    </span>
                    Link to event
                  </a>
                </li>
              )}
              <li className="list-group-item delete-offer-li">
                <Popup
                  trigger={
                    <a href="#" className="text-danger">
                      <span className="d-inline-block text-center" style={{ width: '28px' }}>
                        <i className="fas fa-trash"></i>
                      </span>
                      Delete
                    </a>
                  }
                  modal
                  contentStyle={deleteModalContentStyle}
                  closeOnDocumentClick
                >
                  {(closeDeletePopup) => (
                    <div>
                      <div className="row">
                        <div className="col">
                          <div className="row">
                            <div className="col">
                              <h5 style={{ marginBottom: '16px' }}>
                                <div className="strong">Delete offer?</div>
                              </h5>
                            </div>
                            <div className="col-xs-auto">
                              <a href="#" className="text-dark" style={{ paddingRight: '15px' }} onClick={(e) => { e.preventDefault(); closeDeletePopup(); }}>
                                <i className="fa fa-times"></i>
                              </a>
                            </div>
                          </div>
                          <p>You're about to delete this offer.</p>
                          <p>This cannot be undone.</p>
                          <p>Are you sure?</p>
                          <div className="text-right">
                            <a href="#" onClick={(e) => { e.preventDefault(); closeDeletePopup(); }}>Cancel</a>
                            <a href="#" className="btn btn-danger ml-3" onClick={(e) => { e.preventDefault(); closeDeletePopup(); close(); handleDelete(offerRow); }}>
                              Yes, delete offer
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Popup>
              </li>
            </ul>
          </div>
        </div>
      )}
    </Popup>
  );

  const renderCellContent = (colId, offerRow) => {
    switch (colId) {
      case 'event':
        return (
          <div className="d-flex align-items-center" style={{ width: '100%', position: 'relative', zIndex: 2 }}>
            <div className="team-member-info flex-grow-1" style={{ minWidth: 0 }}>
              {offerRow.artist_image_url ? (
                <img
                  className="img-fluid rounded inline-img"
                  src={offerRow.artist_image_url}
                  alt={offerRow.artist_name || offerRow.artist_or_event_name}
                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
              ) : (
                <CrmAvatar
                  name={offerRow.artist_name || offerRow.artist_or_event_name}
                  title={offerRow.artist_name || offerRow.artist_or_event_name}
                  size={50}
                  className="pipeline-offer-avatar"
                />
              )}
              <div className="team-member-details" style={{ minWidth: 0 }}>
                <a href={offerRow.edit_url} className="strong">
                  {offerRow.artist_or_event_name}
                </a>
                <p className="text-muted mb-0 small" style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                  {offerRow.event_name && offerRow.event_name !== offerRow.artist_or_event_name ? (
                    <>
                      {offerRow.is_confirm && offerRow.confirm_path ? (
                        <a href={offerRow.confirm_path} className="text-muted">{offerRow.event_name}</a>
                      ) : (
                        offerRow.event_name
                      )}
                      {' - '}
                    </>
                  ) : null}
                  {offerRow.start_date_formatted}
                  {' - '}
                  {offerRow.venue_name}
                </p>
              </div>
            </div>
            {isOverflowing && renderRowActions(offerRow)}
          </div>
        );
      case 'offer_created':
        return offerRow.offer_created_on || '';
      case 'owner':
        return offerRow.owner;
      case 'buyer':
        return offerRow.buyer_name || '—';
      case 'status':
        return offerRow.state ? offerRow.state.charAt(0).toUpperCase() + offerRow.state.slice(1) : '';
      case 'type':
        return offerRow.humanized_act_type && offerRow.humanized_act_type !== 'Undefined' ? offerRow.humanized_act_type : '—';
      case 'event_date':
        return offerRow.event_date_formatted || '—';
      case 'event_status':
        return offerRow.has_calendar_event ? (
          <span className="badge badge-pill badge-success">Yes</span>
        ) : (
          <span className="badge badge-pill badge-secondary">No</span>
        );
      case 'holds':
        return offerRow.has_active_holds ? (
          <span className="badge badge-pill badge-success">Yes</span>
        ) : (
          <span className="badge badge-pill badge-secondary">No</span>
        );
      case 'venue_name':
        return offerRow.venue_name ? (
          <div className="team-venue-badges">
            <span className="team-venue-badge">
              <span className="team-venue-dot" style={offerRow.venue_color_hex ? { backgroundColor: offerRow.venue_color_hex } : undefined}></span>
              <span>{offerRow.venue_name}</span>
            </span>
          </div>
        ) : '—';
      case 'genre':
        return offerRow.genres && offerRow.genres.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {offerRow.genres.map((genre) => (
              <span key={genre} className="badge badge-pill badge-light">{genre}</span>
            ))}
          </div>
        ) : '—';
      default:
        return null;
    }
  };

  const renderOfferRow = (offerRow) => (
    <tr key={`${offerRow.ui_version}-${offerRow.id}`}>
      {visibleColumns.map((col) => (
        <td key={col.id} data-label={col.label} className={col.id === 'event' ? 'pipeline-offer-cell' : undefined}>
          {renderCellContent(col.id, offerRow)}
        </td>
      ))}
      {!isOverflowing && (
        <td className="text-right" data-label="">{renderRowActions(offerRow)}</td>
      )}
    </tr>
  );

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
  };

  const handleFilterChange = (filtersOrId, value) => {
    if (typeof filtersOrId === 'object' && value === undefined) {
      setActiveFilters(filtersOrId);
    } else {
      setActiveFilters((prev) => ({ ...prev, [filtersOrId]: value }));
    }
  };

  const handleRemoveFilter = (filterId) => {
    setActiveFilters((prev) => {
      const updated = { ...prev };
      delete updated[filterId];
      return updated;
    });
  };

  const handleApplyFilters = () => {
    setFilterSidebarOpen(false);
  };

  return (
    <div>
      <ToastContainer icon={false} />

      <div className="card">
        <div className="card-body p-0">
          <div className="border-bottom px-4 py-3">
            <div className="d-flex justify-content-between align-items-end crm-toolbar">
              <div className="flex-grow-1 me-3 crm-toolbar-search">
                <CrmSearchInput
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search offers..."
                  ariaLabel="Search offers"
                  useDebounce
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                />
              </div>

              <div className="team-search-filters-right d-flex align-items-center gap-2 crm-toolbar-actions">
                {team.package?.ai_features && processingJobs.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-setlist-secondary"
                    onClick={() => setProcessingJobsSidebarOpen(true)}
                  >
                    <i className={activeJobsCount > 0 ? 'fal fa-spinner fa-spin mr-2' : 'fal fa-check-circle mr-2'} />
                    {activeJobsCount > 0 ? `Processing (${activeJobsCount})` : 'Jobs'}
                  </button>
                )}
                <div style={{ position: 'relative' }}>
                  <SetlistButton
                    variant="secondary"
                    icon={Sliders02}
                    onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
                  >
                    Table Settings
                  </SetlistButton>
                  <TableSettings
                    isOpen={tableSettingsOpen}
                    onClose={() => setTableSettingsOpen(false)}
                    visibleFields={visibleColumns}
                    hiddenFields={hiddenColumns}
                    onFieldsChange={handleColumnsChange}
                  />
                </div>
                <SetlistButton
                  variant="primary"
                  icon={Plus}
                  onClick={() => setModalIsOpen(true)}
                >
                  New Offer
                </SetlistButton>
              </div>
            </div>
          </div>

          <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
            <ActiveFilterBadges
              activeFilters={activeFilters}
              filterGroups={enrichedFilterGroups}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={() => setActiveFilters({})}
              onOpenFilterSidebar={() => setFilterSidebarOpen(true)}
            />

            <span className="text-muted small">
              {(activeFilterCount > 0 || searchTerm) && unfilteredTotal !== null && unfilteredTotal !== totalEntries
                ? `${totalEntries.toLocaleString()} of ${unfilteredTotal.toLocaleString()} offers`
                : `${totalEntries.toLocaleString()} offers`
              }
            </span>
          </div>

          <FilterSidebar
            isOpen={filterSidebarOpen}
            onClose={() => setFilterSidebarOpen(false)}
            onApply={handleApplyFilters}
            title="Filter Offers"
            subtitle="Apply filters to find specific offers."
            filterGroups={enrichedFilterGroups}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
          />

          <CrmTable
            columns={columns}
            className="align-middle pipeline-single-sticky pipeline-offer-cards"
            mobileCards
            stickyFirstColumns={isOverflowing}
            onOverflowChange={setIsOverflowing}
          >
            {loading || error || offers.length === 0 ? (
              <CrmTableStateRow
                colSpan={columns.length}
                loading={loading}
                error={error}
                isEmpty={!loading && !error && offers.length === 0}
                emptyMessage={offerExists ? 'No offers match your search.' : ''}
              />
            ) : (
              offers.map(renderOfferRow)
            )}
          </CrmTable>

          {!loading && !error && offers.length === 0 && !offerExists && renderEmptyState()}
        </div>
      </div>

      {totalPages > 1 && (
        <TeamPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalEntries}
          pageSize={perPage}
          itemLabel="offers"
          onPageChange={(page) => {
            if (!loading && page >= 1 && page <= totalPages) {
              setCurrentPage(page);
            }
          }}
        />
      )}

      <Popup
        open={modalIsOpen}
        contentStyle={modalContentStyle}
        closeOnDocumentClick={false}
        className="offer-modal"
        onClose={closeModal}
      >
        {Object.keys(offer).length > 0 && offer.id && offer.ui_version === 1 ? (
          <OfferModalApp
            offer={offer}
            currentUser={currentUser}
            defaultOffer={{}}
            csrfToken={csrfToken}
            closeModal={closeModal}
            offerTemplates={offerTemplates}
            dataTableEl={null}
            venuesOptions={venuesOptions}
            team={team}
          />
        ) : Object.keys(offer).length > 0 && offer.id && offer.ui_version === 2 ? (
          <OfferV2PreviewApp
            offer={offer}
            currentUser={currentUser}
            csrfToken={csrfToken}
            onClose={closeModal}
            onSuccess={() => {
              closeModal();
              refetch();
              toast.success('Offer has been sent.', {
                position: toast.POSITION.TOP_CENTER,
                draggable: false,
                closeOnClick: false,
                autoClose: 5000,
                hideProgressBar: true,
              });
            }}
            team={team}
          />
        ) : (
          <OfferV2ModalApp
            currentUser={currentUser}
            csrfToken={csrfToken}
            onClose={closeModal}
            onUploadStarted={() => setProcessingJobsSidebarOpen(true)}
            venuesOptions={venuesOptions}
            venueSelected={venueSelected}
            venueOwnerships={venueOwnerships}
            rooms={rooms}
            offerV3={offerV3}
            team={team}
          />
        )}
      </Popup>

      <Popup
        open={Object.keys(offerToLink).length > 0}
        contentStyle={linkOfferModalContentStyle}
        closeOnDocumentClick={false}
        className="offer-modal"
        onClose={() => setOfferToLink({})}
      >
        <LinkOfferModalApp
          currentUser={currentUser}
          csrfToken={csrfToken}
          offer={offerToLink}
          onClose={() => setOfferToLink({})}
          onSuccess={() => {
            refetch();
            setOfferToLink({});
            toast.success('Offer has been updated.', {
              position: toast.POSITION.TOP_CENTER,
              draggable: false,
              closeOnClick: false,
              autoClose: 5000,
              hideProgressBar: true,
            });
          }}
          team={team}
        />
      </Popup>

      <Popup
        open={!!confirmOffer}
        onClose={() => setConfirmOffer(null)}
        modal
        closeOnDocumentClick
        contentStyle={{ border: 'none', borderRadius: '8px', width: '560px', maxWidth: 'calc(100vw - 16px)', padding: 0 }}
      >
        {(close) => (confirmOffer && (
          <V3CreateConfirmModal
            team={team}
            csrfToken={csrfToken}
            offer={confirmOffer}
            onClose={() => { close(); setConfirmOffer(null); }}
            onSuccess={() => {
              close();
              setConfirmOffer(null);
              refetch();
            }}
          />
        ))}
      </Popup>

      <PipelineSlidingPaneApp
        pipelineSlidingPane={{
          pipeline,
          currentUser,
          onClose: () => setPipeline({}),
        }}
      />

      {team.package?.ai_features && (
        <ProcessingJobsSidebar
          isOpen={processingJobsSidebarOpen}
          onClose={() => setProcessingJobsSidebarOpen(false)}
          processingJobs={processingJobs}
          team={team}
          csrfToken={csrfToken}
          onItemClick={(item) => {
            const editUrl = item.type === 'V3Offer'
              ? `/teams/${team.id}/offers/v3/${item.id}/edit?from=offers`
              : `/teams/${team.id}/offers/${item.id}/edit`;
            window.location.href = editUrl;
          }}
          onDeleteJob={(jobId) => {
            setProcessingJobs((prev) => prev.filter((j) => j.id !== jobId));
          }}
        />
      )}
    </div>
  );
};

export default OfferManager;
